import { useCallback } from 'react';
import {
  DeleteVoicemailResponse,
  SetVoicemailViewStatusResponse,
  ViewStatus,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/voicemail/voicemail.pb';
import { UseMutateFunction, useQueryClient } from 'react-query';
import { VoicemailTypes as VmTypes } from '@frontend/api-voicemails';
import * as AdaptoActions from '@frontend/adapto-actions';
import { Actions } from '@frontend/contact-actions';
import { useCustomContactFormSlidePanel } from '@frontend/create-contact-panel';
import { i18next, useTranslation } from '@frontend/i18n';
import { sanitizePhoneNumber } from '@frontend/phone-numbers';
import { useContactPanelShallowStore } from '@frontend/shared';
import { useSlidePanelShallowStore } from '@frontend/slide-panel';
import { useAlert } from '@frontend/design-system';
import { queryKeys } from '../../query-keys';
import { getFormattedCallerName } from '../../utils';
import { VoicemailTableDataRow } from '../all-calls/types';
import { TranscriptionContext } from '../voicemail-details/types';
import { downloadVoicemail as downloadVoicemailUtil } from './voicemail-utils';
import { ForwardVoicemailDynamicData } from './voicemails-multi.component';

export const getFallbackCallerName = (contactNumber: string): string => {
  const sanitizedNumber = sanitizePhoneNumber(contactNumber ?? '');
  return !!sanitizedNumber ? getFormattedCallerName(sanitizedNumber) : i18next.t('Unknown', { ns: 'calls' });
};

export const getShortHandName = (callInfo: VoicemailTableDataRow): string => {
  const { contactName, contactNumber } = callInfo;
  return contactName !== '' ? contactName : getFallbackCallerName(contactNumber);
};

export type VoicemailActionsGetterProps = {
  index: number;
  rowData: VoicemailTableDataRow;
  addTag?: () => void;
  isInsidePopoverDropdown?: boolean;
};

/**
 * Handle as much as we can for actions on voicemail rows.
 * Eventually we will need input from the parent to perform some actions, so we pass those in as props.
 */
export const useVoicemailActionsGetter = ({
  selectedLocationIds,
  searchQuery,
  openForwardVoicemailModal,
  updateReadStatus,
  deleteVoicemail,
  trackingIdPrefix,
}: {
  selectedLocationIds: string[];
  searchQuery: string;
  openForwardVoicemailModal: (props: ForwardVoicemailDynamicData) => void;
  updateReadStatus: UseMutateFunction<SetVoicemailViewStatusResponse, unknown, VmTypes.StatusPayload, unknown>;
  deleteVoicemail: UseMutateFunction<DeleteVoicemailResponse, unknown, VmTypes.DeletePayload, void>;
  trackingIdPrefix?: string;
}) => {
  const { setPersonId } = useContactPanelShallowStore('setPersonId', 'personId');
  const { setShow } = useSlidePanelShallowStore<TranscriptionContext>('setShow');

  const queryClient = useQueryClient();
  const { t } = useTranslation('calls');
  const { setShow: openPanel } = useCustomContactFormSlidePanel();
  const alert = useAlert();

  return useCallback(
    ({ index, rowData, addTag, isInsidePopoverDropdown }: VoicemailActionsGetterProps) =>
      getVoicemailActions({
        trackingIdPrefix,
        rowData,
        isInsidePopoverDropdown,
        viewProfile: () => {
          if (!!rowData.contactId) {
            setPersonId(rowData.contactId, true, selectedLocationIds[0]);
            setShow(true, 'contact');
          }
        },
        createContact: () => {
          const number = rowData.contactNumber;

          const digitsOnly = number ? sanitizePhoneNumber(number) : '';
          const hasCountryCode = digitsOnly.startsWith('1');

          openPanel({
            show: true,
            context: {
              mode: 'create',
              person: {
                MobilePhone: hasCountryCode ? digitsOnly.slice(1) : digitsOnly,
              },
              locationId: rowData.locationIds[0],
              onSave: () => {
                alert.success(t('Contact created successfully.'));

                // This line doesn't quite accomplish anything because the logs do not update with the newly created contact
                queryClient.invalidateQueries({ queryKey: queryKeys.multiVoicemails(searchQuery) });
                queryClient.invalidateQueries({
                  predicate: (query) => query.queryKey.includes('contacts'),
                });
              },
              onError: () => {
                alert.error(t('There was an error creating your contact. Please try again.'));
              },
            },
          });
        },
        forwardVoicemail: () => {
          openForwardVoicemailModal({
            rowIndex: index,
            vmId: rowData.voicemailId == '' ? rowData.channelId : rowData.voicemailId,
            mediaId: rowData.mediaId,
            mediaPath: rowData.mediaFilePath,
            currentVoicemailBoxId: rowData.mailboxId,
            currentVoicemailBoxName: rowData.voiceMailBoxName,
          } as ForwardVoicemailDynamicData);
        },
        addTag,
        downloadVoicemail: () => {
          downloadVoicemailUtil(rowData.mediaFilePath, rowData.contactName ?? 'unknown', rowData.dateTime);
        },
        markAsRead: () => {
          if (!!rowData) {
            updateReadStatus({
              index,
              voicemailId: rowData?.forwardedMessageId || rowData.channelId,
              status: ViewStatus.VIEW_STATUS_READ,
            });
          }
        },
        markAsUnread: () => {
          if (!!rowData) {
            updateReadStatus({
              index,
              voicemailId: rowData?.forwardedMessageId || rowData.channelId,
              status: ViewStatus.VIEW_STATUS_UNREAD,
            });
          }
        },
        deleteVoicemail: () => {
          if (!!rowData) {
            deleteVoicemail({
              voicemailId: rowData?.forwardedMessageId || rowData.channelId,
              index,
              mailboxId: rowData.mailboxId,
            });
          }
        },
      }),
    [
      trackingIdPrefix,
      setPersonId,
      selectedLocationIds,
      setShow,
      openPanel,
      alert,
      t,
      queryClient,
      searchQuery,
      openForwardVoicemailModal,
      updateReadStatus,
      deleteVoicemail,
    ]
  );
};

export const getVoicemailActions = ({
  rowData,
  downloadVoicemail,
  deleteVoicemail,
  viewProfile,
  createContact,
  forwardVoicemail,
  addTag,
  markAsRead,
  markAsUnread,
  isInsidePopoverDropdown,
  trackingIdPrefix = 'phone-portal-weave2.0-voicemails-btn',
}: {
  rowData: VoicemailTableDataRow;
  viewProfile: () => void;
  createContact: () => void;
  forwardVoicemail: () => void;
  addTag?: () => void;
  markAsRead: () => void;
  markAsUnread: () => void;
  deleteVoicemail: () => void;
  downloadVoicemail: () => void;
  isInsidePopoverDropdown?: boolean;
  trackingIdPrefix?: string;
}) => {
  return [
    <Actions.Call
      key='call-action'
      label={`${i18next.t('Call', { ns: 'calls' })} ${getShortHandName(rowData)}`}
      context={{ personId: rowData.contactId, phoneNumber: rowData.contactNumber }}
      trackingIdSuffix={`${trackingIdPrefix}-tablerowaction-call`}
      isInsidePopoverDropdown={isInsidePopoverDropdown}
    />,
    <Actions.Message
      key='message-action'
      label={`${i18next.t('Message', { ns: 'calls' })} ${getShortHandName(rowData)}`}
      context={{ personId: rowData.contactId }}
      trackingIdSuffix={`${trackingIdPrefix}-tablerowaction-message`}
    />,
    rowData.contactId ? (
      <AdaptoActions.Action
        key='view-profile-action'
        icon='user'
        label={i18next.t('View Profile', { ns: 'calls' })}
        trackingId={`${trackingIdPrefix}-tablerowaction-viewprofile`}
        onClick={viewProfile}
      />
    ) : (
      <AdaptoActions.Action
        key='create-contact-action'
        icon='user-management'
        label={i18next.t('Create Contact', { ns: 'calls' })}
        trackingId={`${trackingIdPrefix}-tablerowaction-createcontact`}
        onClick={createContact}
      />
    ),
    <AdaptoActions.Action
      key='forward-voicemail-action'
      icon='voicemail-override'
      label={i18next.t('Forward Voicemail', { ns: 'calls' })}
      trackingId={`${trackingIdPrefix}-tablerowaction-forwardvoicemail`}
      onClick={forwardVoicemail}
    />,
    <AdaptoActions.Action
      key='add-tag-action'
      icon='label'
      label={i18next.t('Add Tag', { ns: 'calls' })}
      trackingId={`${trackingIdPrefix}-tablerowaction-tagvoicemail`}
      onClick={addTag}
    />,
    <AdaptoActions.Action
      key='download-voicemail-action'
      icon='download'
      label={i18next.t('Download Voicemail', { ns: 'calls' })}
      trackingId={`${trackingIdPrefix}-tablerowaction-downloadvoicemail`}
      onClick={downloadVoicemail}
    />,
    rowData.readAt ? (
      <AdaptoActions.Action
        key='mark-unread-action'
        icon='mark-unread'
        label={i18next.t('Mark Unread', { ns: 'calls' })}
        trackingId={`${trackingIdPrefix}-tablerowaction-togglestatus`}
        onClick={markAsUnread}
      />
    ) : (
      <AdaptoActions.Action
        key='mark-read-action'
        icon='mark-read'
        label={i18next.t('Mark Read', { ns: 'calls' })}
        trackingId='phone-portal-weave2.0-voicemails-btn-tablerowaction-togglestatus'
        onClick={markAsRead}
      />
    ),
    <AdaptoActions.Action
      key='delete-voicemail-action'
      icon='trash'
      label={i18next.t('Delete Voicemail', { ns: 'calls' })}
      trackingId='phone-portal-weave2.0-voicemails-btn-tablerowaction-deletevoicemail'
      destructive={true}
      onClick={deleteVoicemail}
    />,
  ];
};
